import { useRef, useEffect, useCallback } from 'react';

const SCALE = 0.4;
const DAMPING = 0.97;

// Default drop params for mouse trail
const TRAIL_RADIUS = 20;
const TRAIL_STRENGTH = 350;

interface WaterRippleApi {
  triggerWave: (clientX: number, clientY: number, strength: number, radius: number) => void;
}

export function useWaterRipple(containerRef: React.RefObject<HTMLDivElement | null>): {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  triggerWave: (clientX: number, clientY: number, strength: number, radius: number) => void;
} {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Stable ref holding the live implementation (assigned inside useEffect)
  const apiRef = useRef<WaterRippleApi>({ triggerWave: () => {} });

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let w = 1;
    let h = 1;
    let buf1 = new Float32Array(1);
    let buf2 = new Float32Array(1);
    let srcData: ImageData;
    let rafId = 0;
    let isHovering = false;
    let prevX: number | null = null;
    let prevY: number | null = null;

    // ─── Build gradient source & buffers ────────────────────────────────────
    function buildBuffers() {
      const rect = container!.getBoundingClientRect();
      w = Math.max(2, Math.floor(rect.width * SCALE));
      h = Math.max(2, Math.floor(rect.height * SCALE));
      canvas!.width = w;
      canvas!.height = h;
      buf1 = new Float32Array(w * h);
      buf2 = new Float32Array(w * h);

      const off = document.createElement('canvas');
      off.width = w;
      off.height = h;
      const oc = off.getContext('2d')!;

      oc.fillStyle = '#1d7c47';
      oc.fillRect(0, 0, w, h);

      const rg = oc.createRadialGradient(w * 0.1, 0, 0, w * 0.1, h * 0.5, w * 0.5);
      rg.addColorStop(0, 'rgba(255,255,255,0.22)');
      rg.addColorStop(1, 'rgba(255,255,255,0)');
      oc.fillStyle = rg;
      oc.fillRect(0, 0, w, h);

      srcData = oc.getImageData(0, 0, w, h);
      ctx!.drawImage(off, 0, 0);
    }

    // ─── Core: add disturbance at canvas-space coordinates ──────────────────
    function addDropAt(cx: number, cy: number, r: number, strength: number) {
      const r2 = r * r;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const d2 = dx * dx + dy * dy;
          if (d2 > r2) continue;
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx < 1 || nx >= w - 1 || ny < 1 || ny >= h - 1) continue;
          buf2[ny * w + nx] += strength * (1 - Math.sqrt(d2) / r);
        }
      }
    }

    // ─── Mouse trail (interpolated so fast moves fill gaps) ─────────────────
    function addDrop(clientX: number, clientY: number) {
      const rect = container!.getBoundingClientRect();
      const cx = (clientX - rect.left) * SCALE;
      const cy = (clientY - rect.top) * SCALE;
      const r = Math.ceil(TRAIL_RADIUS * SCALE);

      if (prevX === null || prevY === null) {
        addDropAt(Math.floor(cx), Math.floor(cy), r, TRAIL_STRENGTH);
      } else {
        const ddx = cx - prevX;
        const ddy = cy - prevY;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy);
        const steps = Math.max(1, Math.ceil(dist / 3));
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          addDropAt(Math.floor(prevX + ddx * t), Math.floor(prevY + ddy * t), r, TRAIL_STRENGTH);
        }
      }
      prevX = cx;
      prevY = cy;
    }

    // ─── External wave trigger (for button hover/click) ──────────────────────
    function doTriggerWave(clientX: number, clientY: number, strength: number, radius: number) {
      const rect = container!.getBoundingClientRect();
      const cx = Math.floor((clientX - rect.left) * SCALE);
      const cy = Math.floor((clientY - rect.top) * SCALE);
      const r = Math.ceil(radius * SCALE);
      addDropAt(cx, cy, r, strength);
      startLoop();
    }

    // Assign live implementation to the stable ref
    apiRef.current = { triggerWave: doTriggerWave };

    // ─── Activity check ──────────────────────────────────────────────────────
    function hasActivity(): boolean {
      for (let i = 0; i < buf2.length; i += 8) {
        if (Math.abs(buf2[i]) > 0.3) return true;
      }
      return false;
    }

    // ─── Simulation + render tick ────────────────────────────────────────────
    function tick() {
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          buf1[i] = (buf2[i - 1] + buf2[i + 1] + buf2[i - w] + buf2[i + w]) / 2 - buf1[i];
          buf1[i] *= DAMPING;
        }
      }
      const tmp = buf1; buf1 = buf2; buf2 = tmp;

      const out = ctx!.createImageData(w, h);
      const src = srcData.data;
      const dst = out.data;

      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          const dx = Math.round((buf2[i - 1] - buf2[i + 1]) * 0.4);
          const dy = Math.round((buf2[i - w] - buf2[i + w]) * 0.4);
          const sx = Math.max(0, Math.min(w - 1, x + dx));
          const sy = Math.max(0, Math.min(h - 1, y + dy));
          const si = (sy * w + sx) * 4;
          const di = i * 4;
          dst[di] = src[si];
          dst[di + 1] = src[si + 1];
          dst[di + 2] = src[si + 2];
          dst[di + 3] = 255;
        }
      }
      ctx!.putImageData(out, 0, 0);

      if (isHovering || hasActivity()) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = 0;
      }
    }

    function startLoop() {
      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    // ─── Events ───────────────────────────────────────────────────────────────
    const onMouseMove = (e: MouseEvent) => {
      isHovering = true;
      addDrop(e.clientX, e.clientY);
      startLoop();
    };
    const onMouseLeave = () => {
      isHovering = false;
      prevX = null;
      prevY = null;
    };
    const onTouchMove = (e: TouchEvent) => {
      for (let i = 0; i < e.touches.length; i++) addDrop(e.touches[i].clientX, e.touches[i].clientY);
      prevX = null;
      prevY = null;
      startLoop();
    };

    const ro = new ResizeObserver(buildBuffers);
    buildBuffers();
    ro.observe(container);
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', onMouseLeave);
    container.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      container.removeEventListener('touchmove', onTouchMove);
      // Reset live implementation to no-op on cleanup
      apiRef.current = { triggerWave: () => {} };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stable wrapper that always delegates to the current live implementation
  const triggerWave = useCallback(
    (clientX: number, clientY: number, strength: number, radius: number) => {
      apiRef.current.triggerWave(clientX, clientY, strength, radius);
    },
    []
  );

  return { canvasRef, triggerWave };
}
